import { useEffect, useMemo, useState } from "preact/hooks";
import { t } from "../i18n";
import type { StaticEvidenceProvider } from "../data/provider";
import type { Career, SkillGroupId } from "../data/schema";
import {
  calculateCareerMatch,
  developmentSequence,
  relatedCareersBySkillOverlap,
  recommendLearningResources,
  selectAssessmentSkills,
} from "../scoring";
import { track } from "../app/analytics";
import { hrefFor, navigate } from "../app/router";
import {
  clearAssessment,
  emptyAssessment,
  loadAssessment,
  saveAssessment,
  type AssessmentState,
} from "../app/storage";
import { buildProfilePayload, printSkillsProfile } from "../app/profile";
import { LeadCapture } from "../components/LeadCapture";
import { SaveResults } from "../components/SaveResults";
import { SkillsProfilePrint } from "../components/SkillsProfilePrint";

const GROUPS: SkillGroupId[] = [
  "scientific-lab",
  "digital-data",
  "research",
  "green-sustainability",
  "entrepreneurial-innovation",
  "transferable-professional",
];

const STAGES = [
  "masters-student",
  "recent-graduate",
  "doctoral-candidate",
  "researcher",
  "academic-staff",
  "other",
] as const;

function careerFromQuery(provider: StaticEvidenceProvider): Partial<AssessmentState> {
  const params = new URLSearchParams(window.location.search);
  const careerId = params.get("career") ?? undefined;
  if (!careerId) return {};
  const career = provider.careerById(careerId);
  if (!career) return {};
  return {
    careerId: career.id,
    clusterId: career.clusterIds[0],
    field: career.disciplineIds.includes("interdisciplinary") ? "interdisciplinary" : career.disciplineIds[0],
    step: 4,
    questionIndex: 0,
  };
}

export function SkillsMatchPage({ provider }: { provider: StaticEvidenceProvider }) {
  const [state, setState] = useState<AssessmentState>(() => {
    const stored = loadAssessment() ?? emptyAssessment();
    return { ...stored, ...careerFromQuery(provider) };
  });

  useEffect(() => {
    track("tool_opened", { tool: "skills-match" });
    track("onboarding_started", { tool: "skills-match" });
  }, []);

  const persist = (next: AssessmentState) => {
    setState(next);
    saveAssessment(next);
  };

  const careers = provider.careers();
  const selectedCareer: Career | undefined = state.careerId ? provider.careerById(state.careerId) : undefined;
  const questions = selectedCareer ? selectAssessmentSkills(selectedCareer) : [];
  const skillsById = provider.skillsById();
  const questionIndex = Math.max(0, Math.min(state.questionIndex ?? 0, Math.max(questions.length - 1, 0)));
  const currentQuestion = questions[questionIndex];
  const currentSkill = currentQuestion ? skillsById.get(currentQuestion.skillId) : undefined;

  const result = useMemo(() => {
    if (!selectedCareer || state.step < 7) return null;
    return calculateCareerMatch(questions, state.answers, skillsById);
  }, [selectedCareer, questions, state.answers, state.step, skillsById]);

  const learning = useMemo(() => {
    if (!result) return [];
    return recommendLearningResources(result.priorities.map((p) => p.skillId), provider.resources());
  }, [result, provider]);

  const profile = useMemo(() => {
    if (!result) return null;
    return buildProfilePayload(state, result, learning.map((r) => r.id));
  }, [result, state, learning]);

  const fieldCareers = careers.filter((career) => {
    if (!state.field) return true;
    if (state.field === "interdisciplinary") {
      return career.disciplineIds.includes("interdisciplinary") || career.disciplineIds.length > 1;
    }
    return career.disciplineIds.includes(state.field) || career.disciplineIds.includes("interdisciplinary");
  });

  const careerOptions = fieldCareers
    .filter((career) => career.clusterIds.includes(state.clusterId as never) || state.clusterId === "exploring")
    .sort((a, b) => {
      if (state.stage === "academic-staff") {
        if (a.id === "car-hei-educator") return -1;
        if (b.id === "car-hei-educator") return 1;
      }
      return a.displayOrder - b.displayOrder;
    })
    .slice(0, 8);

  const progressTotal = 6 + Math.max(questions.length, 1);
  const progressCurrent =
    state.step <= 4
      ? state.step
      : state.step === 5
        ? 5 + questionIndex
        : state.step === 6
          ? 5 + Math.max(questions.length, 1)
          : progressTotal;
  const progressPct = Math.max(4, Math.min(100, Math.round((progressCurrent / progressTotal) * 100)));

  const startOver = () => {
    clearAssessment();
    persist(emptyAssessment());
    navigate("/skills-match", "", true);
  };

  const nextQuestion = () => {
    if (!currentQuestion || state.answers[currentQuestion.skillId] === undefined) return;
    if (questionIndex >= questions.length - 1) {
      persist({ ...state, step: 6, questionIndex });
      return;
    }
    persist({ ...state, questionIndex: questionIndex + 1 });
  };

  const previousQuestion = () => {
    if (questionIndex <= 0) {
      persist({ ...state, step: 4, questionIndex: 0 });
      return;
    }
    persist({ ...state, questionIndex: questionIndex - 1 });
  };

  return (
    <article class="obs-page obs-flow-app obs-wizard">
      <div class="obs-flow-topbar" aria-label={t("flow.match.aria")}>
        <div>
          <p class="obs-flow-step">{t("flow.match.title")}</p>
          <p class="obs-flow-count">{t("flow.match.step", { current: progressCurrent, total: progressTotal })}</p>
        </div>
        <div class="obs-flow-progress" aria-hidden="true">
          <span style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {state.step === 1 && (
        <section class="obs-flow-screen">
          <div class="obs-flow-screen__inner">
            <p class="obs-eyebrow">BOOSTSCIENCE OBSERVATORY</p>
            <h1>{t("flow.match.hero")}</h1>
            <p class="obs-flow-copy">{t("flow.match.hero.body")}</p>
            <div class="obs-flow-options obs-flow-options--3">
              {(["biology", "chemistry", "interdisciplinary"] as const).map((id) => (
                <button
                  key={id}
                  class={`obs-flow-option ${state.field === id ? "is-selected" : ""}`}
                  aria-pressed={state.field === id}
                  type="button"
                  onClick={() => {
                    persist({ ...state, field: id });
                    track("assessment_started", { field: id });
                  }}
                >
                  <span>{t(`radar.${id}`)}</span>
                </button>
              ))}
            </div>
            <div class="obs-flow-actions">
              <button class="obs-btn obs-btn-primary" type="button" disabled={!state.field} onClick={() => persist({ ...state, step: 2 })}>
                {t("match.continue")}
              </button>
            </div>
          </div>
        </section>
      )}

      {state.step === 2 && (
        <section class="obs-flow-screen">
          <div class="obs-flow-screen__inner">
            <p class="obs-flow-step">{t("flow.match.about")}</p>
            <h2>{t("flow.match.about.q")}</h2>
            <p class="obs-flow-copy">{t("flow.match.about.body")}</p>
            <div class="obs-flow-options obs-flow-options--2">
              {STAGES.map((id) => (
                <button
                  key={id}
                  class={`obs-flow-option ${state.stage === id ? "is-selected" : ""}`}
                  type="button"
                  aria-pressed={state.stage === id}
                  onClick={() => persist({ ...state, stage: id })}
                >
                  <span>{t(`match.stage.${id}`)}</span>
                </button>
              ))}
            </div>
            <div class="obs-flow-actions">
              <button class="obs-btn obs-btn-secondary" type="button" onClick={() => persist({ ...state, step: 1 })}>{t("match.back")}</button>
              <button
                class="obs-btn obs-btn-primary"
                type="button"
                onClick={() => persist({
                  ...state,
                  step: 3,
                  clusterId: state.stage === "academic-staff" ? "research-academia" : state.clusterId,
                })}
              >
                {t("match.continue")}
              </button>
            </div>
          </div>
        </section>
      )}

      {state.step === 3 && (
        <section class="obs-flow-screen">
          <div class="obs-flow-screen__inner obs-flow-screen__inner--wide">
            <p class="obs-flow-step">{t("flow.match.direction")}</p>
            <h2>{t("flow.match.direction.q")}</h2>
            <div class="obs-flow-options obs-flow-options--2">
              {provider.catalog.clusters.map((cluster) => (
                <button
                  key={cluster.id}
                  class={`obs-flow-option ${state.clusterId === cluster.id ? "is-selected" : ""}`}
                  type="button"
                  aria-pressed={state.clusterId === cluster.id}
                  onClick={() => persist({ ...state, clusterId: cluster.id, careerId: undefined })}
                >
                  <strong>{cluster.name}</strong>
                  <small>{cluster.shortDefinition}</small>
                </button>
              ))}
            </div>
            {state.clusterId ? (
              <div class="obs-flow-subsection">
                <p class="obs-flow-label">{t("flow.match.pathway")}</p>
                <div class="obs-flow-options obs-flow-options--2">
                  {careerOptions.map((career) => (
                    <button
                      key={career.id}
                      class={`obs-flow-option ${state.careerId === career.id ? "is-selected" : ""}`}
                      type="button"
                      aria-pressed={state.careerId === career.id}
                      onClick={() => {
                        persist({ ...state, careerId: career.id });
                        track("career_selected", { career: career.id });
                      }}
                    >
                      <strong>{career.title}</strong>
                      <small>{career.description}</small>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <div class="obs-flow-actions">
              <button class="obs-btn obs-btn-secondary" type="button" onClick={() => persist({ ...state, step: 2 })}>{t("match.back")}</button>
              <button class="obs-btn obs-btn-primary" type="button" disabled={!state.careerId} onClick={() => persist({ ...state, step: 4 })}>
                {t("match.continue")}
              </button>
            </div>
          </div>
        </section>
      )}

      {state.step === 4 && selectedCareer && (
        <LeadCapture
          tool="skills-match"
          discipline={state.field}
          interest={selectedCareer.id}
          title={t("flow.match.ready")}
          body={t("flow.match.ready.body")}
          onComplete={() => persist({ ...state, step: 5, questionIndex: 0 })}
        />
      )}

      {state.step === 5 && selectedCareer && currentQuestion && currentSkill && (
        <section class="obs-flow-screen obs-question-screen">
          <div class="obs-flow-screen__inner">
            <p class="obs-flow-step">{t("flow.match.competency", { current: questionIndex + 1, total: questions.length })}</p>
            <h2>{currentSkill.name}</h2>
            <p class="obs-flow-copy">{currentSkill.shortDefinition}</p>
            <fieldset class="obs-flow-scale">
              <legend>{t("flow.match.competency.q")}</legend>
              {[0, 1, 2, 3, 4].map((n) => (
                <label key={n} class={state.answers[currentQuestion.skillId] === n ? "is-selected" : undefined}>
                  <input
                    class="obs-sr"
                    type="radio"
                    name={currentQuestion.skillId}
                    value={n}
                    checked={state.answers[currentQuestion.skillId] === n}
                    onChange={() => persist({ ...state, answers: { ...state.answers, [currentQuestion.skillId]: n } })}
                  />
                  <span class="obs-flow-scale__number">{n}</span>
                  <span>{t(`match.scale.${n}`)}</span>
                </label>
              ))}
            </fieldset>
            <div class="obs-flow-actions obs-flow-actions--sticky">
              <button class="obs-btn obs-btn-secondary" type="button" onClick={previousQuestion}>{t("match.back")}</button>
              <button
                class="obs-btn obs-btn-primary"
                type="button"
                disabled={state.answers[currentQuestion.skillId] === undefined}
                onClick={nextQuestion}
              >
                {questionIndex === questions.length - 1 ? t("flow.match.toPriorities") : t("match.continue")}
              </button>
            </div>
          </div>
        </section>
      )}

      {state.step === 6 && (
        <section class="obs-flow-screen">
          <div class="obs-flow-screen__inner">
            <p class="obs-flow-step">{t("flow.match.final")}</p>
            <h2>{t("match.priorities.q")}</h2>
            <p class="obs-flow-copy">{t("match.priorities.help")}</p>
            <div class="obs-flow-options obs-flow-options--2">
              {GROUPS.map((id) => (
                <button
                  key={id}
                  class={`obs-flow-option ${state.priorityGroupIds.includes(id) ? "is-selected" : ""}`}
                  type="button"
                  aria-pressed={state.priorityGroupIds.includes(id)}
                  onClick={() => {
                    const has = state.priorityGroupIds.includes(id);
                    let next = has ? state.priorityGroupIds.filter((x) => x !== id) : [...state.priorityGroupIds, id];
                    if (next.length > 3) next = next.slice(-3);
                    persist({ ...state, priorityGroupIds: next });
                  }}
                >
                  <span>{t(`group.${id}`)}</span>
                </button>
              ))}
            </div>
            <div class="obs-flow-actions">
              <button class="obs-btn obs-btn-secondary" type="button" onClick={() => persist({ ...state, step: 5, questionIndex: Math.max(questions.length - 1, 0) })}>{t("match.back")}</button>
              <button
                class="obs-btn obs-btn-primary"
                type="button"
                onClick={() => {
                  persist({ ...state, step: 7, completedAt: new Date().toISOString() });
                  track("assessment_completed", { career: state.careerId ?? "" });
                }}
              >
                {t("flow.match.viewProfile")}
              </button>
            </div>
          </div>
        </section>
      )}

      {state.step === 7 && selectedCareer && result && (
        <section class="obs-results obs-results--flow">
          <p class="obs-eyebrow">{t("flow.match.profileEyebrow")}</p>
          <h1>{selectedCareer.title}</h1>
          <div class="obs-results-score">
            <p class="obs-score">{Math.round(result.percent)}<span>%</span></p>
            <div>
              <p class="obs-score-kicker">{t("match.results.score")}</p>
              <p class="obs-band">{t(`match.results.band.${result.band}`)}</p>
              <p>{t(`match.results.lead.${result.band}`)}</p>
            </div>
          </div>
          <div class="obs-cat-bars">
            {result.categoryScores.map((cat) => (
              <div key={cat.groupId}>
                <p>{t(`group.${cat.groupId}`)}<b>{Math.round(cat.percent)}%</b></p>
                <div class="obs-bar" aria-hidden="true"><span style={{ width: `${Math.round(cat.percent)}%` }} /></div>
              </div>
            ))}
          </div>
          <div class="obs-results-split">
            <div>
              <h3>{t("match.results.strengths")}</h3>
              <ul class="obs-list">
                {result.strengths.map((s) => <li key={s.skillId}>{skillsById.get(s.skillId)?.name}</li>)}
              </ul>
            </div>
            <div>
              <h3>{t("match.results.priorities")}</h3>
              {result.priorities.map((s) => {
                const skill = skillsById.get(s.skillId);
                if (!skill) return null;
                const boosted = state.priorityGroupIds.includes(skill.groupId);
                return (
                  <article class="obs-priority" key={s.skillId}>
                    <h4>{skill.name}</h4>
                    <p class="obs-badge">{s.priority >= 8 || boosted ? t("match.priority.high") : t("match.priority.medium")}</p>
                    <p>{skill.whyItMatters}</p>
                    <a href={hrefFor("/radar", `?type=skill&entity=${skill.id}`)}>{t("match.results.exploreSkill")}</a>
                  </article>
                );
              })}
            </div>
          </div>
          <h3>{t("match.results.path")}</h3>
          {learning.map((resource) => (
            <article class="obs-priority" key={resource.id}>
              <h4>{resource.title}</h4>
              <p>{resource.description}</p>
              {resource.url ? (
                <a href={resource.url} onClick={() => track("learning_resource_clicked", { id: resource.id })}>{resource.title}</a>
              ) : (
                <p class="obs-soon">{t("match.comingSoon")}</p>
              )}
            </article>
          ))}
          <h3>{t("match.results.related")}</h3>
          <ul class="obs-list">
            {relatedCareersBySkillOverlap(selectedCareer, careers).map((career) => (
              <li key={career.id}>
                <button
                  class="obs-linkish"
                  type="button"
                  onClick={() => persist({
                    ...state,
                    careerId: career.id,
                    clusterId: career.clusterIds[0],
                    step: 4,
                    questionIndex: 0,
                    answers: {},
                  })}
                >
                  {career.title}
                </button>
              </li>
            ))}
          </ul>
          <h3>{t("match.results.sequence")}</h3>
          <ol class="obs-list">
            {developmentSequence(result.priorities, skillsById).map((item) => <li key={item}>{item}</li>)}
          </ol>
          {profile ? <SaveResults profile={profile} /> : null}
          <div class="obs-actions">
            <button class="obs-btn obs-btn-primary" type="button" onClick={() => printSkillsProfile()}>{t("match.results.print")}</button>
            <a class="obs-btn obs-btn-secondary" href={hrefFor("/radar")}>{t("match.results.radar")}</a>
            <button class="obs-btn obs-btn-secondary" type="button" onClick={startOver}>{t("match.startOver")}</button>
          </div>
        </section>
      )}
      {state.step === 7 && profile ? <SkillsProfilePrint profile={profile} provider={provider} /> : null}
    </article>
  );
}
